/**
 * Aircraft State Tracker Module
 * Tracks aircraft state for start/finish detection and race timing
 */

import {
  isInsideSector,
  isBehindStartLine,
  doesSegmentCrossStartLine,
  haversineDistance,
  calculateOptimalRemainingDistance,
  calculateMaximumRemainingDistance,
  projectedDistance,
  calculateBearing,
  normalizeAngle
} from './distanceCalculator.js';

const AAT_SCORE_EPSILON_M = 5;
const REGATTA_START_CLUSTER_WINDOW_MS = 60 * 1000;
const FINISH_CROSSING_BRACKET_STEPS = 16;
const FINISH_CROSSING_BINARY_STEPS = 12;
const FINISH_SEGMENT_SCAN_STEPS = 64;

function resolvePilotDisplayName(position = null) {
  if (!position) {
    return null;
  }

  return (
    [position.id_fname, position.id_lname].filter(Boolean).join(' ').trim() ||
    position.pilotName ||
    position.pilot_name ||
    null
  );
}

function resolveFlightPilotName(flight = null) {
  if (!flight) {
    return null;
  }

  const structuredName = [flight.pilot?.first_name, flight.pilot?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  return (
    flight._soaringSpotPilotName ||
    flight.pilot?.name ||
    structuredName ||
    resolvePilotDisplayName(flight.positions?.[0]) ||
    null
  );
}

/**
 * Aircraft State Tracker Class
 * Manages state for each aircraft including start/finish detection
 */
export class AircraftStateTracker {
  constructor() {
    this.aircraftStates = new Map(); // cookie -> state object
    this.precomputedEvents = new Map(); // cookie -> replay event summary, including AAT score updates
    this.silentMode = false; // Suppress console logs during state rebuild
    this.finishLeader = null; // Track first finisher and finish time
    this.finishOrderCounter = 0; // Sequential finish ordering
    this.finishOrderMap = new Map(); // cookie -> { order, deltaMinutes, finishTimeMs, elapsedSeconds, startTimeMs }
    this.finishLeaderElapsedSeconds = null; // Elapsed seconds for the winner
    this.regattaReferenceStartMs = null; // Earliest validated regatta start
  }

  /**
   * Get or initialize aircraft state
   * @param {number} cookie - Aircraft cookie
   * @returns {object} Aircraft state object
   */
  getState(cookie) {
    if (!this.aircraftStates.has(cookie)) {
      this.aircraftStates.set(cookie, {
        cookie,

        // Race status
        raceStatus: 'pre_start', // 'pre_start' | 'racing' | 'finished'
        startTime: null,
        startTimeMs: null,
        finishTime: null,
        elapsedTime_s: 0,

        // Task progress
        currentLeg: 0, // 0 = before start, 1 = start→TP1, etc.
        lastTPReached: -1, // Index of last TP touched (-1 = none)

        // State flags for transition detection
        wasInsideStartSector: false,
        wasBehindStartLine: null, // For line-crossing start detection (null = unknown)
        wasInsideCurrentTP: false,
        wasInsideFinish: false,

        // Distance tracking
        officialDistance_km: 0,  // Condor-style scored distance (start/finish wall points, intermediate TP centers)
        realDistance_km: 0,      // Task-optimized distance (uses optimal TP points) - for XC speed
        officialRemainingDistance_km: 0,
        realRemainingDistance_km: 0,
        aatScoredPoints: [],
        aatScoredDistances_m: [],

        // Re-start tracking
        restartCount: 0,

        // Last position for reference
        lastPosition: null,
        lastFinishPosition: null,

        // Finish metadata
        finishOrder: null,
        finishDeltaMinutes: null,
        finishDeltaMinutesHandicap: null,
        finishTimeMs: null,
        xcSpeedKmh: null
      });
    }

    return this.aircraftStates.get(cookie);
  }

  isAatTask(task) {
    return task?.task_type === 'AAT';
  }

  isCylinderTurnpoint(turnpoint) {
    return Number(turnpoint?.Angle) > 180;
  }

  getTurnpointPoint(turnpoint) {
    return {
      lat: turnpoint.Lat,
      lon: turnpoint.Lon
    };
  }

  getTaskPoint(task, tpIndex) {
    const turnpoint = task?.turnpoints?.[tpIndex];
    if (!turnpoint) {
      return null;
    }

    const scoredLat = Number(turnpoint.scoredLat);
    const scoredLon = Number(turnpoint.scoredLon);
    if (Number.isFinite(scoredLat) && Number.isFinite(scoredLon)) {
      return {
        lat: scoredLat,
        lon: scoredLon
      };
    }

    return this.getTurnpointPoint(turnpoint);
  }

  getPositionPoint(position) {
    return {
      lat: position.lat,
      lon: position.lon
    };
  }

  clearTurnpointEntryFlags(state) {
    Object.keys(state).forEach(key => {
      if (key.startsWith('wasInsideTP_')) {
        delete state[key];
      }
    });
  }

  resetDistanceTracking(state) {
    state.officialDistance_km = 0;
    state.realDistance_km = 0;
    state.officialRemainingDistance_km = 0;
    state.realRemainingDistance_km = 0;
    state.aatScoredPoints = [];
    state.aatScoredDistances_m = [];
  }

  getAatScoredPoint(state, task, tpIndex) {
    if (tpIndex <= 0 || tpIndex >= task.turnpoints.length - 1) {
      return this.getTaskPoint(task, tpIndex);
    }

    return state.aatScoredPoints[tpIndex] || this.getTurnpointPoint(task.turnpoints[tpIndex]);
  }

  getAatScoringReferencePoints(state, task, tpIndex, candidatePoint) {
    const previousPoint =
      tpIndex === 1
        ? this.getTaskPoint(task, 0)
        : this.getAatScoredPoint(state, task, tpIndex - 1);
    const nextPoint = this.getTaskPoint(task, tpIndex + 1);

    const candidateScore_m =
      haversineDistance(previousPoint.lat, previousPoint.lon, candidatePoint.lat, candidatePoint.lon) +
      haversineDistance(candidatePoint.lat, candidatePoint.lon, nextPoint.lat, nextPoint.lon);

    return { previousPoint, nextPoint, candidateScore_m };
  }

  updateAatScoredTurnpoint(position, task) {
    if (!this.isAatTask(task)) {
      return;
    }

    const state = this.getState(position.cookie);
    const tpIndex = state.lastTPReached;

    if (state.raceStatus !== 'racing' || tpIndex < 1 || tpIndex >= task.turnpoints.length - 1) {
      return;
    }

    const turnpoint = task.turnpoints[tpIndex];
    const candidatePoint = this.getPositionPoint(position);

    if (!isInsideSector(candidatePoint, turnpoint)) {
      return;
    }

    const { candidateScore_m } = this.getAatScoringReferencePoints(state, task, tpIndex, candidatePoint);
    const previousScore_m = state.aatScoredDistances_m[tpIndex];

    if (
      !Number.isFinite(previousScore_m) ||
      candidateScore_m > previousScore_m + AAT_SCORE_EPSILON_M
    ) {
      state.aatScoredPoints[tpIndex] = candidatePoint;
      state.aatScoredDistances_m[tpIndex] = candidateScore_m;
    }
  }

  buildAatTravelPath(state, task, currentPosition = null, finishPoint = null) {
    const pathPoints = [this.getTaskPoint(task, 0)];
    const lastCompletedIndex = Math.min(state.lastTPReached, task.turnpoints.length - 2);

    for (let i = 1; i <= lastCompletedIndex; i++) {
      pathPoints.push(this.getAatScoredPoint(state, task, i));
    }

    if (finishPoint) {
      pathPoints.push(finishPoint);
    } else if (currentPosition) {
      pathPoints.push(this.getPositionPoint(currentPosition));
    }

    return pathPoints;
  }

  calculatePathDistanceKm(points) {
    if (!Array.isArray(points) || points.length < 2) {
      return 0;
    }

    let totalDistance_m = 0;

    for (let i = 1; i < points.length; i++) {
      totalDistance_m += haversineDistance(
        points[i - 1].lat,
        points[i - 1].lon,
        points[i].lat,
        points[i].lon
      );
    }

    return totalDistance_m / 1000;
  }

  getAatRemainingTurnpoints(state, task, position = null) {
    let nextTPIndex = Math.max(0, state.currentLeg);
    const lastReachedIndex = Number.isInteger(state?.lastTPReached) ? state.lastTPReached : -1;
    const finishIndex = (task?.turnpoints?.length || 0) - 1;

    // While still inside the currently scored AAT sector, keep that sector in the
    // remaining-route bounds so projected AAT distance can still use the room
    // available deeper in the same cylinder.  Once the glider leaves it, the
    // sector drops out again automatically unless they re-enter later.
    if (
      position &&
      lastReachedIndex >= 1 &&
      lastReachedIndex < finishIndex &&
      isInsideSector(this.getPositionPoint(position), task.turnpoints[lastReachedIndex])
    ) {
      nextTPIndex = Math.min(nextTPIndex, lastReachedIndex);
    }

    if (nextTPIndex >= task.turnpoints.length) {
      return [];
    }

    return task.turnpoints.slice(nextTPIndex);
  }

  getAatRemainingDistanceBounds(position, task) {
    if (!position || !task?.turnpoints?.length) {
      return {
        minimumRemainingDistance_km: 0,
        maximumRemainingDistance_km: 0
      };
    }

    const state = this.getState(position.cookie);
    const remainingTurnpoints = this.getAatRemainingTurnpoints(state, task, position);
    if (!remainingTurnpoints.length) {
      return {
        minimumRemainingDistance_km: 0,
        maximumRemainingDistance_km: 0
      };
    }

    const currentPoint = this.getPositionPoint(position);
    const minimumRemainingDistance_km =
      calculateOptimalRemainingDistance(currentPoint, remainingTurnpoints, position.cookie) / 1000;
    const maximumRemainingDistance_km =
      calculateMaximumRemainingDistance(currentPoint, remainingTurnpoints) / 1000;

    return {
      minimumRemainingDistance_km: Math.max(0, minimumRemainingDistance_km),
      maximumRemainingDistance_km: Math.max(
        Math.max(0, minimumRemainingDistance_km),
        maximumRemainingDistance_km
      )
    };
  }

  calculateAatRemainingDistanceKm(position, task, state) {
    if (!position || !task?.turnpoints?.length) {
      return 0;
    }

    const nextTPIndex = Math.max(0, state.currentLeg);
    if (nextTPIndex >= task.turnpoints.length) {
      return 0;
    }

    let totalDistance_m = 0;
    let previousPoint = this.getPositionPoint(position);
    const finishIndex = task.turnpoints.length - 1;

    for (let i = nextTPIndex; i < task.turnpoints.length; i++) {
      const currentPoint =
        i === finishIndex
          ? this.getTaskPoint(task, i)
          : this.getTurnpointPoint(task.turnpoints[i]);
      totalDistance_m += haversineDistance(
        previousPoint.lat,
        previousPoint.lon,
        currentPoint.lat,
        currentPoint.lon
      );
      previousPoint = currentPoint;
    }

    return totalDistance_m / 1000;
  }

  calculateAatDistance(position, task) {
    const state = this.getState(position.cookie);

    this.updateAatScoredTurnpoint(position, task);

    const pathPoints = this.buildAatTravelPath(state, task, position);
    const officialDistance_km = Math.max(0, this.calculatePathDistanceKm(pathPoints));

    state.officialDistance_km = officialDistance_km;
    state.realDistance_km = officialDistance_km;
    state.officialRemainingDistance_km = this.calculateAatRemainingDistanceKm(position, task, state);
    state.realRemainingDistance_km = state.officialRemainingDistance_km;

    return {
      official_km: state.officialDistance_km,
      real_km: state.realDistance_km,
      remaining_km: state.officialRemainingDistance_km
    };
  }

  finalizeAatFinishDistance(position, task) {
    const state = this.getState(position.cookie);
    const travelledPathPoints = this.buildAatTravelPath(
      state,
      task,
      null,
      this.getTaskPoint(task, task.turnpoints.length - 1)
    );
    const travelledDistance_km = this.calculatePathDistanceKm(travelledPathPoints);

    state.officialDistance_km = Math.max(0, travelledDistance_km);
    state.realDistance_km = state.officialDistance_km;
    state.officialRemainingDistance_km = 0;
    state.realRemainingDistance_km = 0;
  }

  /**
   * Check if aircraft crosses START line (LEAVING the start sector)
   * Start is triggered by going from INSIDE → OUTSIDE while below max altitude
   *
   * @param {object} position - Aircraft position {lat, lon, altitude_m, cookie}
   * @param {object} startSector - Start sector definition
   * @param {number} maxStartAltitude_m - Maximum start altitude
   * @param {number|null} startGateTimeMs - Ignore any starts before this timestamp
   * @param {object|null} task - Task definition used for start direction validation
   * @param {object} options - Optional behavior flags
   * @returns {boolean} True if start crossed
   */
  checkStartCrossing(position, startSector, maxStartAltitude_m, startGateTimeMs = null, task = null, options = {}) {
    const state = this.getState(position.cookie);
    const { lat, lon, altitude_m, cookie } = position;
    const eventTimeMs = extractTimestampMs(position);
    const startHeightAllowed = Number.isFinite(startGateTimeMs) || altitude_m <= maxStartAltitude_m;
    const isStartLine = startSector.isLine === true && startSector.bearing != null;
    const hasStartGate = Number.isFinite(startGateTimeMs);
    const afterStartGate = !hasStartGate || eventTimeMs >= startGateTimeMs;
    const requireDirectionTowardNextTp = options?.requireDirectionTowardNextTp === true;

    // Once a pilot has completed the task, keep that finish locked and do not
    // allow a later pass through the start to reset the race state.
    if (state.raceStatus === 'finished') {
      if (!isStartLine) {
        state.wasInsideStartSector = isInsideSector({ lat, lon }, startSector);
      }
      state.lastPosition = position;
      return false;
    }

    let previousPositionForStart = state.lastPosition;
    let previousInsideStart = state.wasInsideStartSector;

    if (
      hasStartGate &&
      previousPositionForStart &&
      Number.isFinite(eventTimeMs)
    ) {
      const previousMs = extractTimestampMs(previousPositionForStart);
      if (Number.isFinite(previousMs) && previousMs < startGateTimeMs && eventTimeMs >= startGateTimeMs) {
        const fraction = Math.max(0, Math.min(1, (startGateTimeMs - previousMs) / (eventTimeMs - previousMs)));
        const gatePosition = interpolateTrackPosition(previousPositionForStart, position, fraction);
        previousPositionForStart = gatePosition;
        if (!isStartLine) {
          previousInsideStart = isInsideSector({ lat: gatePosition.lat, lon: gatePosition.lon }, startSector);
        }
      }
    }

    let crossedStart = false;

    if (isStartLine) {
      // LINE CROSSING detection for 180° start sectors.
      // Uses segment intersection: checks if the track between prev and current
      // position crosses the start line within the start radius.
      if (previousPositionForStart && startHeightAllowed && afterStartGate) {
        crossedStart = doesSegmentCrossStartLine(
          { lat: previousPositionForStart.lat, lon: previousPositionForStart.lon },
          { lat, lon },
          startSector
        );
      }
    } else {
      // Standard sector exit detection for non-line sectors. Any inside-to-outside
      // transition counts, including exits through a semicircle perimeter.
      const nowInside = isInsideSector({ lat, lon }, startSector);
      crossedStart = previousInsideStart && !nowInside && startHeightAllowed && afterStartGate;

      state.wasInsideStartSector = nowInside;
    }

    if (
      crossedStart &&
      requireDirectionTowardNextTp &&
      !isHeadingTowardNextTurnpoint(previousPositionForStart, position, task)
    ) {
      crossedStart = false;
    }

    // LIVE MODE FIX: If this is the first position we've seen for this aircraft
    // and it's already OUTSIDE the start sector, infer that it has already started.
    // This handles the case where we connect to a live race mid-flight.
    const isFirstPosition = state.lastPosition === null;
    if (isFirstPosition && state.raceStatus === 'pre_start') {
      const nowInside = isStartLine
        ? (isBehindStartLine({ lat, lon }, startSector) && haversineDistance(lat, lon, startSector.Lat, startSector.Lon) <= startSector.RadiusMeters)
        : isInsideSector({ lat, lon }, startSector);
      if (!nowInside && afterStartGate) {
        // Aircraft is outside start sector on first update - assume already racing
        crossedStart = true;
      }
    }

    if (
      crossedStart &&
      requireDirectionTowardNextTp &&
      !isHeadingTowardNextTurnpoint(previousPositionForStart, position, task)
    ) {
      crossedStart = false;
    }

    if (crossedStart) {
      const isRestart = state.startTime !== null;

      if (!this.silentMode) {

        if (isRestart) {
        }
      }

      const startTimeIso = new Date(eventTimeMs).toISOString();

      // Update state
      state.raceStatus = 'racing';
      state.startTime = startTimeIso;
      state.startTimeMs = eventTimeMs;
      state.startAltitude_m = altitude_m; // Store start altitude for XC speed calculation
      state.currentLeg = 1; // Now on first leg (start → TP1)
      state.restartCount += isRestart ? 1 : 0;
      state.elapsedTime_s = 0;
      state.finishOrder = null;
      state.finishDeltaMinutes = null;
      state.finishTime = null;
      state.finishTimeMs = null;
      state.wasInsideFinish = false;
      state.lastFinishPosition = null;

      // Reset distance tracking on (re)start
      this.resetDistanceTracking(state);
      state.lastTPReached = -1;
      this.clearTurnpointEntryFlags(state);
    }

    state.lastPosition = position;

    return crossedStart;
  }

  /**
   * Check if aircraft crosses FINISH line
   * Phase 3 Update: Only allow finish if all intermediate TPs have been reached
   *
   * @param {object} position - Aircraft position {lat, lon, altitude_m, cookie}
   * @param {object} finishSector - Finish sector definition
   * @param {object} task - Complete task object
   * @param {number} minFinishAltitude_m - Minimum finish altitude (optional)
   * @returns {boolean} True if finish crossed
   */
  checkFinishCrossing(position, finishSector, task, minFinishAltitude_m = null) {
    const state = this.getState(position.cookie);
    const { cookie } = position;
    const previousFinishPosition = state.lastFinishPosition;
    const nowInside = isPositionInsideFinish(position, finishSector, minFinishAltitude_m);
    const finishEntryMs = findFinishEntryMs(
      previousFinishPosition,
      position,
      finishSector,
      minFinishAltitude_m,
      state.wasInsideFinish
    );

    // Can only finish if racing
    if (state.raceStatus !== 'racing') {
      return false;
    }

    // Phase 3: Must have reached all intermediate turnpoints before finishing
    // lastTPReached should be second-to-last TP (finish is last TP)
    const requiredTPIndex = task.turnpoints.length - 2;
    if (state.lastTPReached < requiredTPIndex) {
      state.wasInsideFinish = nowInside;
      state.lastFinishPosition = position;
      return false; // Haven't completed all legs yet
    }

    // Detect entry into the finish sector. This scans the segment between GPS
    // samples as well as the current point so small FAI sectors are not missed.
    const crossedFinish = Number.isFinite(finishEntryMs);

    if (crossedFinish) {
      const finishEventMs = finishEntryMs;
      const finishTime = new Date(finishEventMs).toISOString();
      let elapsedSeconds = state.elapsedTime_s ?? 0;
      const startTimeMs =
        state.startTimeMs ??
        (state.startTime ? new Date(state.startTime).getTime() : null);

      if (startTimeMs !== null && Number.isFinite(startTimeMs)) {
        const elapsedMs = finishEventMs - startTimeMs;
        if (elapsedMs >= 0) {
          elapsedSeconds = Math.floor(elapsedMs / 1000);
        }
      }

      if (!this.silentMode) {
        if (minFinishAltitude_m) {
        }
      }

      if (this.isAatTask(task)) {
        this.finalizeAatFinishDistance(position, task);
      } else {
        // Set final distances
        state.officialDistance_km = task.scoringTaskLength || 0;
        state.realDistance_km = task.netTaskLength || 0;
        state.officialRemainingDistance_km = 0;
        state.realRemainingDistance_km = 0;
      }

      if (!this.silentMode) {
      }

      // Update state
      state.raceStatus = 'finished';
      state.finishTime = finishTime;
      state.finishTimeMs = finishEventMs;
      state.elapsedTime_s = elapsedSeconds;

      const existingFinishMeta = this.finishOrderMap.get(state.cookie);

      if (existingFinishMeta && Number.isFinite(existingFinishMeta.order)) {
        state.finishOrder = existingFinishMeta.order;
      }

      if (state.finishOrder === null) {
        state.finishOrder = ++this.finishOrderCounter;
      }

      this.finishOrderCounter = Math.max(this.finishOrderCounter, state.finishOrder || 0);

      // Freeze delta relative to first finisher using task times
      const isRegattaTask = task?.regatta_start === true;
      if (!this.finishLeader) {
        this.finishLeader = {
          cookie: state.cookie,
          finishTimeMs: state.finishTimeMs
        };
        this.finishLeaderElapsedSeconds = Number.isFinite(elapsedSeconds) ? elapsedSeconds : null;
        state.finishDeltaMinutes = 0;
      } else {
        const leaderFinishMs = this.finishLeader?.finishTimeMs ?? null;
        let deltaMinutes = null;

        if (isRegattaTask && leaderFinishMs !== null) {
          deltaMinutes = (state.finishTimeMs - leaderFinishMs) / 60000;
        } else if (
          !isRegattaTask &&
          this.finishLeaderElapsedSeconds !== null &&
          Number.isFinite(elapsedSeconds)
        ) {
          deltaMinutes = (elapsedSeconds - this.finishLeaderElapsedSeconds) / 60;
        } else if (leaderFinishMs !== null) {
          deltaMinutes = (state.finishTimeMs - leaderFinishMs) / 60000;
        }

        if (Number.isFinite(deltaMinutes)) {
          state.finishDeltaMinutes = Math.max(0, deltaMinutes);
        } else {
          state.finishDeltaMinutes = null;
        }
      }

      // Persist finish metadata for reuse (e.g., replay reconstruction)
      // Cache CN and glider type so finished pilots can still show on leaderboard after leaving server
      this.finishOrderMap.set(state.cookie, {
        order: state.finishOrder,
        deltaMinutes: state.finishDeltaMinutes,
        deltaMinutesHandicap: state.finishDeltaMinutesHandicap ?? null,
        finishTimeMs: state.finishTimeMs,
        elapsedSeconds: Number.isFinite(state.elapsedTime_s) ? state.elapsedTime_s : null,
        startTimeMs: startTimeMs,
        startAltitude_m: Number.isFinite(state.startAltitude_m) ? state.startAltitude_m : null,
        cn: position.id_cn || position.callsign,
        pilotName: resolvePilotDisplayName(position),
        gliderType: position.aircraft_type || position.aircraft || position.id_model,
        xcSpeedKmh: Number.isFinite(state.xcSpeedKmh) ? state.xcSpeedKmh : null,
        officialDistance_km: Number.isFinite(state.officialDistance_km) ? state.officialDistance_km : null,
        realDistance_km: Number.isFinite(state.realDistance_km) ? state.realDistance_km : null
      });
    }

    // Update state flag
    state.wasInsideFinish = nowInside;
    state.lastFinishPosition = position;
    state.lastPosition = position;

    return crossedFinish;
  }

  /**
   * Check if aircraft touches a turnpoint
   * Phase 3: Turnpoint Detection
   *
   * @param {object} position - Aircraft position {lat, lon, altitude_m, cookie}
   * @param {object} task - Complete task object with turnpoints and bonuses
   * @returns {boolean} True if turnpoint was touched
   */
  checkTurnpointTouch(position, task) {
    const state = this.getState(position.cookie);
    const { lat, lon, cookie } = position;

    // Can only touch TPs if racing
    if (state.raceStatus !== 'racing') {
      return false;
    }

    // Get next turnpoint to reach
    let nextTPIndex = state.lastTPReached + 1;

    // Skip start (index 0) - it's handled by checkStartCrossing
    if (nextTPIndex === 0) {
      nextTPIndex = 1; // Move to first intermediate TP
    }

    // Check if we've reached the finish (last TP)
    if (nextTPIndex >= task.turnpoints.length - 1) {
      return false; // Finish is handled separately
    }

    const nextTP = task.turnpoints[nextTPIndex];

    // Check if inside this turnpoint
    const nowInside = isInsideSector({ lat, lon }, nextTP);

    // Detect entry (was outside, now inside)
    // IMPORTANT: Track state per TP index, not globally
    const stateKey = `wasInsideTP_${nextTPIndex}`;
    const wasInside = state[stateKey] || false;
    const justEntered = !wasInside && nowInside;

    if (justEntered) {
      if (!this.silentMode) {
        console.log(`   Leg: ${state.currentLeg} → ${state.currentLeg + 1}`);
      }

      // Capture distances BEFORE touching TP
      const beforeOfficial = state.officialDistance_km;
      const beforeReal = state.realDistance_km;

      // Update state - mark TP as reached
      state.lastTPReached = nextTPIndex;
      state.currentLeg += 1;

      // Recalculate distances now that TP is reached
      this.calculateDistance(position, task);
      const afterOfficial = state.officialDistance_km;
      const afterReal = state.realDistance_km;

      if (!this.silentMode) {
        console.log(`   Real/optimized distance: ${beforeReal.toFixed(2)} → ${afterReal.toFixed(2)} km`);
      }
    }

    // Update state flag for this specific TP
    state[stateKey] = nowInside;

    return justEntered;
  }

  /**
   * Calculate task distance from aircraft position
   * Phase 3: Distance Calculation
   *
   * IMPORTANT: How Condor Calculates Distance
   *
   * Condor's "official" distance = progress along the current scored leg plus completed scored legs.
   * Start/finish cylinders use their fixed scored wall points; intermediate legs remain TP-center based.
   *
   * When you touch a TP cylinder (anywhere on the edge):
   * - Distance JUMPS because it stops counting distance to THIS TP center
   * - Starts counting from current position to NEXT TP center
   * - The "bonus" is the distance saved by not having to fly to the center of the TP you just tagged
   *
   * Example: If you're 10km from TP1 center, TP1 radius is 500m:
   * - Before: distance includes the 10km to TP1 center + TP1→TP2 + TP2→finish
   * - Touch cylinder at edge (9.5km from center): distance now = your position→TP2 center + TP2→finish
   * - Bonus applied ≈ 9.5km (you saved not having to fly to TP1 center)
   *
   * Real distance: Net/optimized task progress used for XC-style speed calculations.
   * Official distance: Condor-style scored progress along the current task leg.
   *
   * @param {object} position - Aircraft position {lat, lon, altitude_m, cookie}
   * @param {object} task - Complete task object with cumulative distances
   * @returns {object} Distance data {official_km, real_km, remaining_km}
   */
  calculateDistance(position, task) {
    const state = this.getState(position.cookie);
    const { lat, lon } = position;

    // Before start
    if (state.currentLeg === 0) {
      state.officialDistance_km = 0;
      state.realDistance_km = 0;
      state.officialRemainingDistance_km = task.scoringTaskLength || 0;
      state.realRemainingDistance_km = task.netTaskLength || 0;
      return {
        official_km: 0,
        real_km: 0,
        remaining_km: state.officialRemainingDistance_km
      };
    }

    // After finish
    if (state.raceStatus === 'finished') {
      // Don't recalculate - use final values set at finish
      return {
        official_km: state.officialDistance_km,
        real_km: state.realDistance_km,
        remaining_km: 0
      };
    }

    if (this.isAatTask(task)) {
      return this.calculateAatDistance(position, task);
    }

    // DISTANCE CALCULATION: Remaining distance approach
    // Formula: flown_distance = total_task_distance - remaining_distance_to_finish
    //
    // Official distance: Uses TP centers (what Condor calculates)
    // Real distance: Uses optimal points on TP cylinders (for XC speed)

    const nextTPIndex = state.currentLeg; // Next TP to reach
    const fullTaskDistance_km = task.scoringTaskLength || 0;
    let optimalRemaining_km = 0;

    if (nextTPIndex >= task.turnpoints.length) {
      // Past finish
      state.officialDistance_km = fullTaskDistance_km;
      state.realDistance_km = task.netTaskLength || 0;
      state.officialRemainingDistance_km = 0;
      state.realRemainingDistance_km = 0;
    } else {
      // === OFFICIAL DISTANCE (projected onto course line) ===
      // XCSoar method: Project current position onto the course line between previous TP and next TP
      // This measures forward progress along the course, not lateral deviations

      const remainingTPs = task.turnpoints.slice(nextTPIndex);

      // Get the previous TP (the one we're coming from)
      const prevTPIndex = nextTPIndex - 1;
      const prevPoint = this.getTaskPoint(task, prevTPIndex);
      const nextPoint = this.getTaskPoint(task, nextTPIndex);

      // Calculate projected distance along current leg (from prevTP to nextTP)
      const projectedDist_m = projectedDistance(
        prevPoint.lat,
        prevPoint.lon,
        nextPoint.lat,
        nextPoint.lon,
        lat,
        lon
      );

      // Distance flown on current leg
      const currentLegFlown_km = projectedDist_m / 1000;

      // Distance already completed (up to previous TP)
      const previousDistance_km = task.cumulative[prevTPIndex];

      // Official distance = distance to previous TP + progress along current leg
      state.officialDistance_km = Math.max(0, previousDistance_km + currentLegFlown_km);

      // === REAL DISTANCE (to optimal points on TP cylinders) ===
      // Real distance uses optimal routing through remaining TPs
      // The "bonus" emerges naturally from the geometry - no need to track explicitly

      const optimalRemaining_m = calculateOptimalRemainingDistance({ lat, lon }, remainingTPs, position.cookie);
      optimalRemaining_km = optimalRemaining_m / 1000;

      // Real distance uses NET task length (optimal total) as baseline
      // Net task length = minimum possible distance with perfect TP touches
      const netTaskDistance_km = task.netTaskLength || 0;
      const rawRealDistance_km = Math.max(0, netTaskDistance_km - optimalRemaining_km);
      const previousReal_km = state.realDistance_km ?? 0;

      if (rawRealDistance_km >= previousReal_km - 1e-6) {
        state.realDistance_km = rawRealDistance_km;
      } else if (previousReal_km - rawRealDistance_km > 1.0) {
        state.realDistance_km = rawRealDistance_km;
      }

      // DEBUG: Log distance calculations at race start (opt-in only)
      // if (state.currentLeg === 1 && state.officialDistance_km < 1.0 && Math.random() < 0.02) {
      //   console.log(`🔍 DEBUG: Distance calculation at race start for ${position.cookie}:`);
      //   console.log(`   Current leg: ${state.currentLeg}`);
      //   console.log(`   Previous TP: ${prevTP.Name} at cumulative ${previousDistance_km.toFixed(2)} km`);
      //   console.log(`   Next TP: ${nextTP.Name}`);
      //   console.log(`   Projected distance on current leg: ${currentLegFlown_km.toFixed(2)} km`);
      //   console.log(`   Optimal remaining: ${optimalRemaining_km.toFixed(2)} km`);
      //   console.log(`   Difference: ${Math.abs(state.officialDistance_km - state.realDistance_km).toFixed(2)} km`);
      // }
    }

    // Remaining distance to finish (based on official distance)
    const remaining_km = fullTaskDistance_km - state.officialDistance_km;
    state.officialRemainingDistance_km = Math.max(0, remaining_km);
    state.realRemainingDistance_km = Math.max(0, optimalRemaining_km);

    return {
      official_km: state.officialDistance_km,
      real_km: state.realDistance_km,
      remaining_km: state.officialRemainingDistance_km
    };
  }

  /**
   * Update elapsed time for racing aircraft
   * @param {number} cookie - Aircraft cookie
   */
  updateElapsedTime(cookie, timestamp = null) {
    const state = this.getState(cookie);

    if (state.raceStatus === 'racing' && state.startTimeMs !== null) {
      const coercedTimestamp = coerceToTimestampMs(timestamp);
      let currentMs = Number.isFinite(coercedTimestamp) ? coercedTimestamp : null;

      if (currentMs === null || !Number.isFinite(currentMs)) {
        currentMs = state.lastPosition ? extractTimestampMs(state.lastPosition) : Date.now();
      }

      const elapsedMs = currentMs - state.startTimeMs;
      if (elapsedMs >= 0) {
        state.elapsedTime_s = Math.floor(elapsedMs / 1000);
      }
    }
  }

  /**
   * Get formatted elapsed time
   * @param {number} cookie - Aircraft cookie
   * @returns {string} Formatted time (HH:MM:SS)
   */
  getFormattedElapsedTime(cookie) {
    const state = this.getState(cookie);
    return formatElapsedTime(state.elapsedTime_s);
  }

  /**
   * Pre-compute all task events (start/TP/finish) for an aircraft by analyzing entire track
   * This is done ONCE when replay loads, then used for instant state lookup during scrubbing
   *
   * @param {string} cookie - Aircraft cookie
   * @param {Array} positions - Array of ALL position objects (sorted by timestamp)
   * @param {object} task - Task object with turnpoints
   * @param {number} maxStartAltitude_m - Max start altitude
   * @param {number|null} startGateTimeMs - Ignore any starts before this timestamp
   */
  precomputeEvents(cookie, positions, task, maxStartAltitude_m = 999999, startGateTimeMs = null, startFloorMs = null) {
    if (!positions || positions.length === 0 || !task || !task.turnpoints) {
      return;
    }

    const effectiveStartGateTimeMs = Number.isFinite(startGateTimeMs)
      ? startGateTimeMs
      : resolveTaskStartGateMs(task);

    const events = {
      startTime: null,          // Best valid start (resolved after regatta clustering)
      startAltitude_m: null,    // Altitude at start for XC speed calculation
      startCandidates: [],      // All detected start crossings [{time, altitude_m, belowMaxAlt, beforeAnyTP}]
      tpTimes: [],              // Array of TP touch times [TP1_time, TP2_time, ...]
      finishTime: null,         // Earliest valid finish (after all TPs)
      runs: [],                 // Replay start runs for restart-aware state reconstruction
      aatScoredPointEvents: buildEmptyReplayAatEventBuckets(task),
      aatFinishOfficialDistance_km: null,
      aatFinishRealDistance_km: null
    };

    // Initialize TP times array (skip start=0 and finish=last)
    const numIntermediateTPs = task.turnpoints.length - 2;
    for (let i = 0; i < numIntermediateTPs; i++) {
      events.tpTimes.push(null);
    }

    // Temporary state for detection
    const tempState = {
      wasInsideStartSector: false,
      lastPosition: null // For segment-based line crossing
    };

    const startSector = task.turnpoints[0];
    const finishSector = task.turnpoints[task.turnpoints.length - 1];
    const isStartLine = startSector.isLine === true && startSector.bearing != null;

    // Single pass through all positions
    for (const position of positions) {
      const { lat, lon, altitude_m } = position;
      const eventTimeMs = extractTimestampMs(position);

      // === START DETECTION ===
      const startHeightAllowed = Number.isFinite(effectiveStartGateTimeMs) || altitude_m <= maxStartAltitude_m;
      const distFromStart = haversineDistance(lat, lon, startSector.Lat, startSector.Lon);
      let crossedStart = false;
      const hasStartGate = Number.isFinite(effectiveStartGateTimeMs);
      const afterStartGate = !hasStartGate || eventTimeMs >= effectiveStartGateTimeMs;
      let previousPositionForStart = tempState.lastPosition;
      let previousInsideStart = tempState.wasInsideStartSector;

      if (
        hasStartGate &&
        previousPositionForStart &&
        Number.isFinite(eventTimeMs)
      ) {
        const previousMs = extractTimestampMs(previousPositionForStart);
        if (Number.isFinite(previousMs) && previousMs < effectiveStartGateTimeMs && eventTimeMs >= effectiveStartGateTimeMs) {
          const fraction = Math.max(0, Math.min(1, (effectiveStartGateTimeMs - previousMs) / (eventTimeMs - previousMs)));
          const gatePosition = interpolateTrackPosition(previousPositionForStart, position, fraction);
          previousPositionForStart = gatePosition;
          if (!isStartLine) {
            previousInsideStart = isInsideSector({ lat: gatePosition.lat, lon: gatePosition.lon }, startSector);
          }
        }
      }

      if (isStartLine) {
        // LINE CROSSING detection for 180° start sectors.
        // Uses segment intersection: checks if the track between prev and current
        // position crosses the start line within the start radius.
        if (previousPositionForStart && afterStartGate && startHeightAllowed) {
          crossedStart = doesSegmentCrossStartLine(
            { lat: previousPositionForStart.lat, lon: previousPositionForStart.lon },
            { lat, lon },
            startSector
          );
        }
      } else {
        // Standard sector exit detection for non-line sectors. Any inside-to-outside
        // transition counts, including exits through a semicircle perimeter.
        const nowInsideStart = isInsideSector({ lat, lon }, startSector);
        crossedStart = previousInsideStart && !nowInsideStart && afterStartGate && startHeightAllowed;

        tempState.wasInsideStartSector = nowInsideStart;
      }
      tempState.lastPosition = position;

      if (crossedStart) {
        // Record ALL start crossings — replay run resolution will determine which one survives.
        const candidateTime = new Date(eventTimeMs);
        events.startCandidates.push({
          time: candidateTime,
          altitude_m,
          belowMaxAlt: startHeightAllowed,
          beforeAnyTP: true
        });
      }
    }

    events.runs = buildReplayRunsFromCandidates(positions, task, events.startCandidates, startFloorMs);
    applyReplayRunToEvents(events, selectAuthoritativeReplayRun(events.runs));

    // Store precomputed events
    this.precomputedEvents.set(cookie, events);

    // console.log(`📊 Precomputed events for ${cookie}:`, {
    //   start: events.startTime?.toISOString(),
    //   tps: events.tpTimes.map((t, i) => `TP${i + 1}: ${t?.toISOString() || 'not reached'}`),
    //   finish: events.finishTime?.toISOString()
    // });
  }

  /**
   * Rebuild state for an aircraft by replaying position history up to target time
   * DEPRECATED: Use precomputeEvents + reconstructStateFromEvents for better performance
   *
   * @param {string} cookie - Aircraft cookie
   * @param {Array} positions - Array of position objects (sorted by timestamp)
   * @param {Date} targetTime - Target time to rebuild state up to
   * @param {object} task - Task object with turnpoints
   * @param {number} maxStartAltitude_m - Max start altitude
   */
  rebuildState(cookie, positions, targetTime, task, maxStartAltitude_m = 999999) {
    // Reset state completely
    this.aircraftStates.delete(cookie);

    if (!positions || positions.length === 0 || !task) {
      return;
    }

    // Filter positions up to target time
    const relevantPositions = positions.filter(pos =>
      new Date(pos.timestamp) <= targetTime
    );

    if (relevantPositions.length === 0) {
      return;
    }

    // Enable silent mode to suppress console spam during replay
    const previousSilentMode = this.silentMode;
    this.silentMode = true;

    const startGateTimeMs = resolveTaskStartGateMs(task);

    try {
      // Replay all positions in chronological order with detection logic
      const startSector = task.turnpoints?.[0];
      const finishSector = task.turnpoints?.[task.turnpoints.length - 1];

      for (const position of relevantPositions) {
        // Run all detection checks in order (same as updatePositions)
        if (startSector) {
          this.checkStartCrossing(position, startSector, maxStartAltitude_m, startGateTimeMs, task);
        }

        if (task.turnpoints && task.turnpoints.length > 0) {
          this.checkTurnpointTouch(position, task);
          this.calculateDistance(position, task);
        }

        if (finishSector) {
          this.checkFinishCrossing(position, finishSector, task);
        }
      }
    } finally {
      // Restore previous silent mode
      this.silentMode = previousSilentMode;
    }

    const state = this.getState(cookie);
    if (!this.silentMode) {
      console.log(`🔄 State rebuilt for ${cookie} up to ${targetTime.toISOString()}:`, {
        raceStatus: state.raceStatus,
        currentLeg: state.currentLeg,
        lastTPReached: state.lastTPReached,
        officialDistance_km: state.officialDistance_km.toFixed(2),
        realDistance_km: state.realDistance_km.toFixed(2)
      });
    }
  }

  /**
   * Reconstruct state from precomputed events at a specific time (FAST - O(n) where n = # TPs)
   * This is used during scrubbing for instant state lookup
   *
   * @param {string} cookie - Aircraft cookie
   * @param {Date} targetTime - Target time to reconstruct state for
   * @param {object} task - Task object with turnpoints
   * @param {object} position - Current position (for distance calculation)
   */
  reconstructStateFromEvents(cookie, targetTime, task, position) {
    const events = this.precomputedEvents.get(cookie);
    if (!events) {
      // No precomputed events - reset state
      this.aircraftStates.delete(cookie);
      return;
    }

    // Reset and rebuild state based on events
    this.aircraftStates.delete(cookie);
    const state = this.getState(cookie);

    const targetMs = targetTime.getTime();
    const activeRun = task?.regatta_start ? null : resolveReplayRunAtTime(events, targetMs);
    const activeEvents = activeRun || events;
    const activeTpTimes = Array.isArray(activeEvents?.tpTimes) ? activeEvents.tpTimes : [];
    const startTimeMs = activeEvents?.startTime ? activeEvents.startTime.getTime() : null;
    const finishTimeMs = activeEvents?.finishTime ? activeEvents.finishTime.getTime() : null;
    const hasStarted = startTimeMs !== null && startTimeMs <= targetMs;
    const hasFinished = finishTimeMs !== null && finishTimeMs <= targetMs;

    if (hasStarted) {
      state.raceStatus = 'racing';
      state.startTime = activeEvents.startTime.toISOString();
      state.startTimeMs = startTimeMs;
      state.startAltitude_m = activeEvents.startAltitude_m || 0; // Store start altitude
      state.currentLeg = 1;
      state.lastTPReached = 0; // Started means passed start TP

      // Check which TPs have been reached
      for (let i = 0; i < activeTpTimes.length; i++) {
        const tpTime = activeTpTimes[i];
        if (tpTime && tpTime.getTime() <= targetMs) {
          state.lastTPReached = i + 1; // TP index (1-based for intermediate TPs)
          state.currentLeg = i + 2; // Leg after this TP
        } else {
          break; // TPs are sequential, stop at first not reached
        }
      }

      if (this.isAatTask(task)) {
        this.restoreAatReplayScoredState(state, activeEvents, targetMs, task);
      }
    } else {
      state.raceStatus = 'pre_start';
      state.currentLeg = 0;
      state.lastTPReached = -1;
      state.startTime = null;
      state.startTimeMs = null;
      state.elapsedTime_s = 0;
    }

    if (hasFinished) {
      state.raceStatus = 'finished';
      state.finishTime = activeEvents.finishTime.toISOString();
      state.finishTimeMs = finishTimeMs;

      const finishMeta = this.finishOrderMap.get(cookie);
      const fallbackAatOfficialDistance_km = Number.isFinite(activeEvents?.aatFinishOfficialDistance_km)
        ? activeEvents.aatFinishOfficialDistance_km
        : null;
      const fallbackAatRealDistance_km = Number.isFinite(activeEvents?.aatFinishRealDistance_km)
        ? activeEvents.aatFinishRealDistance_km
        : fallbackAatOfficialDistance_km;
      if (finishMeta) {
        state.finishOrder = finishMeta.order;
        state.finishDeltaMinutes = finishMeta.deltaMinutes;
        state.finishDeltaMinutesHandicap = finishMeta.deltaMinutesHandicap ?? null;
        state.officialDistance_km = Number.isFinite(finishMeta.officialDistance_km)
          ? finishMeta.officialDistance_km
          : (task?.task_type === 'AAT'
            ? fallbackAatOfficialDistance_km
            : (task.scoringTaskLength || 0));
        state.realDistance_km = Number.isFinite(finishMeta.realDistance_km)
          ? finishMeta.realDistance_km
          : (task?.task_type === 'AAT'
            ? fallbackAatRealDistance_km
            : (task.netTaskLength || 0));
        if (Number.isFinite(finishMeta.elapsedSeconds)) {
          state.elapsedTime_s = finishMeta.elapsedSeconds;
        }
        if (Number.isFinite(finishMeta.startTimeMs)) {
          state.startTimeMs = finishMeta.startTimeMs;
        }
        if (Number.isFinite(finishMeta.xcSpeedKmh)) {
          state.xcSpeedKmh = finishMeta.xcSpeedKmh;
        }
      } else {
        state.finishOrder = null;
        state.finishDeltaMinutes = null;
        state.officialDistance_km = task?.task_type === 'AAT'
          ? fallbackAatOfficialDistance_km
          : (task.scoringTaskLength || 0);
        state.realDistance_km = task?.task_type === 'AAT'
          ? fallbackAatRealDistance_km
          : (task.netTaskLength || 0);
      }

      state.officialRemainingDistance_km = 0;
      state.realRemainingDistance_km = 0;

      if (state.startTimeMs !== null) {
        const elapsedMs = state.finishTimeMs - state.startTimeMs;
        if (elapsedMs >= 0) {
          state.elapsedTime_s = Math.max(0, Math.floor(elapsedMs / 1000));
        }
      } else if (!Number.isFinite(state.elapsedTime_s)) {
        state.elapsedTime_s = 0;
      }
    } else if (hasStarted && position) {
      // Racing - calculate elapsed time and distance
      if (state.startTimeMs !== null) {
        const elapsedMs = targetMs - state.startTimeMs;
        if (elapsedMs >= 0) {
          state.elapsedTime_s = Math.floor(elapsedMs / 1000);
        }
      }
      this.calculateDistance(position, task);
    }

    if (position) {
      state.lastPosition = position;
    }
  }

  restoreAatReplayScoredState(state, activeEvents, targetMs, task) {
    state.aatScoredPoints = [];
    state.aatScoredDistances_m = [];

    if (!this.isAatTask(task) || !Number.isFinite(targetMs)) {
      return;
    }

    const pointEvents = Array.isArray(activeEvents?.aatScoredPointEvents)
      ? activeEvents.aatScoredPointEvents
      : [];
    const maxTpIndex = Math.min(state.lastTPReached, task.turnpoints.length - 2);

    for (let tpIndex = 1; tpIndex <= maxTpIndex; tpIndex += 1) {
      const latestEvent = getLatestReplayAatScoreEvent(pointEvents[tpIndex], targetMs);
      if (!latestEvent?.point) {
        continue;
      }

      state.aatScoredPoints[tpIndex] = {
        lat: latestEvent.point.lat,
        lon: latestEvent.point.lon
      };

      if (Number.isFinite(latestEvent.score_m)) {
        state.aatScoredDistances_m[tpIndex] = latestEvent.score_m;
      }
    }
  }

  /**
   * Precompute events for all aircraft (called ONCE when replay loads)
   *
   * @param {Array} flights - Array of flight objects with positions
   * @param {object} task - Task object with turnpoints
   * @param {number} maxStartAltitude_m - Max start altitude
   */
  precomputeAllEvents(flights, task, maxStartAltitude_m = 999999) {
    if (!flights || flights.length === 0 || !task) {
      return;
    }

    this.resetFinishTracking();

// DEBUG:     console.log(`📊 Precomputing events for ${flights.length} flights...`);
    const startTime = performance.now();

    const startGateTimeMs = resolveTaskStartGateMs(task);
    const knownRegattaStartMs = this.resolveKnownRegattaStartMs(task);

    for (const flight of flights) {
      const cookie = flight.positions?.[0]?.cookie;
      if (cookie && flight.positions) {
        this.precomputeEvents(cookie, flight.positions, task, maxStartAltitude_m, startGateTimeMs, knownRegattaStartMs);
      }
    }

    this.precomputedEvents.forEach((events, cookie) => {
      applyReplayRunToEvents(events, selectAuthoritativeReplayRun(events.runs));

      if (!events.startTime && events.startCandidates.length > 0) {
        const candidatesBeforeTp = filterEligibleStartCandidates(events, knownRegattaStartMs);
        const validBeforeTp = candidatesBeforeTp.filter((candidate) => candidate.belowMaxAlt);
        const best =
          validBeforeTp[validBeforeTp.length - 1] ||
          candidatesBeforeTp[candidatesBeforeTp.length - 1] ||
          (Number.isFinite(knownRegattaStartMs) ? null : events.startCandidates[0]);
        if (best) {
          events.startTime = best.time;
          events.startAltitude_m = best.altitude_m;
        }
      }
    });

    // Regatta detection: find a time window where the most DISTINCT pilots
    // each have at least one crossing. Use a sliding window approach.
    const pilotCrossings = []; // [{ms, cookie}] sorted by ms
    this.precomputedEvents.forEach((events, cookie) => {
      const candidatesBeforeTp = filterEligibleStartCandidates(events, knownRegattaStartMs);
      for (const c of candidatesBeforeTp) {
        pilotCrossings.push({ ms: c.time.getTime(), cookie });
      }
    });
    pilotCrossings.sort((a, b) => a.ms - b.ms);

    const regattaCluster = findDominantPilotCluster(pilotCrossings, REGATTA_START_CLUSTER_WINDOW_MS);

    if (!task.regatta_start && !hasExplicitNonRegattaStartWindow(task) && regattaCluster) {
      if (hasSufficientRegattaStartCluster(regattaCluster, flights.length)) {
        task.regatta_start = true;
      }
    }

    // Regatta start refinement: pick each pilot's LAST crossing within the cluster window
    if (task?.regatta_start && regattaCluster && regattaCluster.distinctPilots >= 2) {
      const clusterPadMs = Math.min(15 * 1000, Math.floor(REGATTA_START_CLUSTER_WINDOW_MS / 4));
      const windowStart = regattaCluster.earliestMs - clusterPadMs;
      const windowEnd = regattaCluster.latestMs + clusterPadMs;

      this.precomputedEvents.forEach((events, cookie) => {
        if (events.startCandidates.length === 0) return;
        const candidatesBeforeTp = filterEligibleStartCandidates(events, knownRegattaStartMs);
        // Find the LAST candidate within the cluster window
        let bestCandidate = null;
        for (const c of candidatesBeforeTp) {
          const ms = c.time.getTime();
          if (ms >= windowStart && ms <= windowEnd) {
            bestCandidate = c;
          }
        }
        if (bestCandidate) {
          events.startTime = bestCandidate.time;
          events.startAltitude_m = bestCandidate.altitude_m;
        }
      });

    }

    if (task?.regatta_start) {
      this.computeRegattaReferenceStart(task);
    }

    const finishEntries = [];
    this.precomputedEvents.forEach((events, cookie) => {
      if (events?.finishTime) {
        const finishTimeMs = events.finishTime.getTime();
        const startTimeMs = events.startTime ? events.startTime.getTime() : null;
        const elapsedSeconds =
          startTimeMs !== null
            ? Math.max(0, Math.floor((finishTimeMs - startTimeMs) / 1000))
            : null;

        // Find the flight for this cookie to get CN and glider type
        const flight = flights.find(f => f.positions?.[0]?.cookie === cookie);
        const cn = flight?.pilot?.callsign || null;
        const pilotName = resolveFlightPilotName(flight);
        const gliderType = flight?.aircraft?.model_name || null;

        finishEntries.push({
          cookie,
          finishTimeMs,
          startTimeMs,
          startAltitude_m: Number.isFinite(events?.startAltitude_m) ? events.startAltitude_m : null,
          elapsedSeconds,
          cn,
          pilotName,
          gliderType,
          officialDistance_km: Number.isFinite(events?.aatFinishOfficialDistance_km)
            ? events.aatFinishOfficialDistance_km
            : null,
          realDistance_km: Number.isFinite(events?.aatFinishRealDistance_km)
            ? events.aatFinishRealDistance_km
            : null
        });
      }
    });

    finishEntries.sort((a, b) => a.finishTimeMs - b.finishTimeMs);

    const isRegattaTask = task?.regatta_start === true;
    const leaderFinishMs = finishEntries.length > 0 ? finishEntries[0].finishTimeMs : null;
    const leaderElapsedSeconds =
      finishEntries.length > 0 ? finishEntries[0].elapsedSeconds : null;
    this.finishOrderMap = new Map();
    this.finishOrderCounter = 0;

    finishEntries.forEach((entry, index) => {
      const order = index + 1;
      let deltaMinutes = null;

      if (isRegattaTask && leaderFinishMs !== null) {
        deltaMinutes = (entry.finishTimeMs - leaderFinishMs) / 60000;
      } else if (leaderElapsedSeconds !== null && entry.elapsedSeconds !== null) {
        deltaMinutes = (entry.elapsedSeconds - leaderElapsedSeconds) / 60;
      } else if (leaderFinishMs !== null) {
        deltaMinutes = (entry.finishTimeMs - leaderFinishMs) / 60000;
      }

      if (Number.isFinite(deltaMinutes)) {
        deltaMinutes = Math.max(0, deltaMinutes);
      } else {
        deltaMinutes = null;
      }

      this.finishOrderMap.set(entry.cookie, {
        order,
        deltaMinutes,
        deltaMinutesHandicap: null,
        finishTimeMs: entry.finishTimeMs,
        elapsedSeconds: entry.elapsedSeconds,
        startTimeMs: entry.startTimeMs,
        startAltitude_m: Number.isFinite(entry.startAltitude_m) ? entry.startAltitude_m : null,
        cn: entry.cn,
        pilotName: entry.pilotName,
        gliderType: entry.gliderType,
        xcSpeedKmh: null,
        officialDistance_km: task?.task_type === 'AAT'
          ? (Number.isFinite(entry.officialDistance_km) ? entry.officialDistance_km : null)
          : (task?.scoringTaskLength || 0),
        realDistance_km: task?.task_type === 'AAT'
          ? (Number.isFinite(entry.realDistance_km) ? entry.realDistance_km : null)
          : (task?.netTaskLength || 0)
      });

      this.finishOrderCounter = Math.max(this.finishOrderCounter, order);

      if (index === 0) {
        this.finishLeader = {
          cookie: entry.cookie,
          finishTimeMs: entry.finishTimeMs
        };
        this.finishLeaderElapsedSeconds = entry.elapsedSeconds ?? null;
      }
    });

    const elapsed = performance.now() - startTime;
    this.computeRegattaReferenceStart(task);
  }

  /**
   * Rebuild state for all aircraft using precomputed events (FAST - used during seek)
   *
   * @param {Array} flights - Array of flight objects with positions
   * @param {Date} targetTime - Target time to rebuild state up to
   * @param {object} task - Task object with turnpoints
   */
  reconstructAllStatesFromEvents(flights, targetTime, task) {
    if (!flights || flights.length === 0 || !task) {
      return;
    }

    for (const flight of flights) {
      const cookie = flight.positions?.[0]?.cookie;
      if (cookie) {
        // Use the latest known sample at or before the seek time for distance calculation.
        const position = findLatestReplayPositionAtOrBeforeTime(flight.positions, targetTime);
        this.reconstructStateFromEvents(cookie, targetTime, task, position);
      }
    }
  }

  /**
   * Rebuild state for all aircraft (used during seek operations)
   * DEPRECATED: Use precomputeAllEvents (once) + reconstructAllStatesFromEvents (on seek)
   *
   * @param {Array} flights - Array of flight objects with positions
   * @param {Date} targetTime - Target time to rebuild state up to
   * @param {object} task - Task object with turnpoints
   * @param {number} maxStartAltitude_m - Max start altitude
   */
  rebuildAllStates(flights, targetTime, task, maxStartAltitude_m = 999999) {
    if (!flights || flights.length === 0 || !task) {
      return;
    }

    for (const flight of flights) {
      const cookie = flight.positions?.[0]?.cookie;
      if (cookie && flight.positions) {
        this.rebuildState(cookie, flight.positions, targetTime, task, maxStartAltitude_m);
      }
    }
  }

  /**
   * Reset state for an aircraft (e.g., on task change)
   * @param {number} cookie - Aircraft cookie
   */
  resetState(cookie) {
    this.aircraftStates.delete(cookie);
    console.log(`🔄 State reset for aircraft ${cookie}`);
  }

  /**
   * Clear all aircraft states
   */
  clearAll() {
    this.aircraftStates.clear();
    this.resetFinishTracking();
    console.log('🔄 All aircraft states cleared');
  }

  /**
   * Get all aircraft states (for stats widget)
   * @returns {Map} Map of cookie -> state
   */
  getAllStates() {
    return this.aircraftStates;
  }

  /**
   * Reset finish tracking metadata (orders, leader, frozen deltas)
   * @param {boolean} resetStateFields - When true, also clear finish data on existing states
   */
  resetFinishTracking(resetStateFields = false) {
    this.finishLeader = null;
    this.finishOrderCounter = 0;
    this.finishOrderMap = new Map();
    this.finishLeaderElapsedSeconds = null;
    this.regattaReferenceStartMs = null;

    if (resetStateFields) {
      this.aircraftStates.forEach(state => {
        state.finishOrder = null;
        state.finishDeltaMinutes = null;
        state.finishDeltaMinutesHandicap = null;
        state.finishTimeMs = null;
        state.xcSpeedKmh = null;
        state.startTimeMs = null;
        state.elapsedTime_s = 0;
      });
    }
  }

  /**
   * Get finished pilot information from cache
   * Used to show finished pilots on leaderboard even after they've left the server
   * @param {string} cookie - Aircraft cookie
   * @returns {object|null} Finish info with {order, deltaMinutes, finishTimeMs, elapsedSeconds, cn, gliderType}
   */
  getFinishedPilotInfo(cookie) {
    return this.finishOrderMap.get(cookie) || null;
  }

  /**
   * Get all finished pilot cookies
   * @returns {Array<string>} Array of cookies for pilots who finished
   */
  getAllFinishedPilotCookies() {
    return Array.from(this.finishOrderMap.keys());
  }

  /**
   * Compute regatta reference start from explicit metadata or the dominant start cluster.
   * @param {object} task - Task definition
   */
  computeRegattaReferenceStart(task) {
    this.regattaReferenceStartMs = null;
    if (!task?.regatta_start) {
      return;
    }

    const explicitStartMs = this.resolveKnownRegattaStartMs(task);
    if (Number.isFinite(explicitStartMs)) {
      this.regattaReferenceStartMs = explicitStartMs;
      return;
    }

    const clusteredStart = this.resolveClusteredRegattaStartMs(this.collectRegattaStartCandidatesFromEvents());
    if (Number.isFinite(clusteredStart)) {
      this.regattaReferenceStartMs = clusteredStart;
    }
  }

  /**
   * Get cached regatta reference start (ms)
   * @returns {number|null}
   */
  getRegattaReferenceStartTime() {
    return this.regattaReferenceStartMs ?? null;
  }

  resolveKnownRegattaStartMs(task) {
    if (!task?.regatta_start) {
      return null;
    }

    const explicitStartMs = this.resolveOfficialRegattaStartMs(task);
    const manualStartMs = resolveTaskStartGateMs(task);

    if (Number.isFinite(explicitStartMs) && Number.isFinite(manualStartMs)) {
      return Math.max(explicitStartMs, manualStartMs);
    }
    if (Number.isFinite(explicitStartMs)) {
      return explicitStartMs;
    }
    if (Number.isFinite(manualStartMs)) {
      return manualStartMs;
    }

    return null;
  }

  resolveOfficialRegattaStartMs(task) {
    const candidates = [
      task?.official_regatta_start_time,
      task?.official_regatta_start_iso
    ];

    for (const candidate of candidates) {
      if (!candidate) continue;
      const candidateMs = new Date(candidate).getTime();
      if (Number.isFinite(candidateMs)) {
        return candidateMs;
      }
    }

    return null;
  }

  shouldTreatTaskAsRegatta(task, totalPilotCount = null) {
    if (task?.regatta_start === true) {
      return true;
    }

    if (hasExplicitNonRegattaStartWindow(task)) {
      return false;
    }

    if (Number.isFinite(this.resolveOfficialRegattaStartMs(task))) {
      return true;
    }

    const validStarts = this.collectRegattaStartCandidatesFromEvents();

    if (validStarts.length < 2) {
      return false;
    }

    const cluster = findDominantStartCluster(validStarts);
    const fieldSize = Number.isFinite(totalPilotCount) ? totalPilotCount : validStarts.length;
    return hasSufficientRegattaStartCluster(cluster, fieldSize);
  }

  shouldTreatTaskAsRegattaFromStates(task, totalPilotCount = null) {
    if (task?.regatta_start === true) {
      return true;
    }

    if (hasExplicitNonRegattaStartWindow(task)) {
      return false;
    }

    if (Number.isFinite(this.resolveOfficialRegattaStartMs(task))) {
      return true;
    }

    const validStarts = this.collectRegattaStartCandidatesFromStates();
    if (validStarts.length < 2) {
      return false;
    }

    const cluster = findDominantStartCluster(validStarts);
    const fieldSize = Number.isFinite(totalPilotCount) ? totalPilotCount : this.aircraftStates.size;
    return hasSufficientRegattaStartCluster(cluster, fieldSize);
  }

  /**
   * Fallback: derive regatta start from live states when precompute not available
   * @param {object} task - Task definition
   * @returns {number|null} Reference start timestamp in ms
   */
  ensureRegattaReferenceFromStates(task) {
    if (!task?.regatta_start) {
      return null;
    }

    const explicitStartMs = this.resolveKnownRegattaStartMs(task);
    if (Number.isFinite(explicitStartMs)) {
      this.regattaReferenceStartMs = explicitStartMs;
      return this.regattaReferenceStartMs;
    }

    const clusteredStart = this.resolveClusteredRegattaStartMs(
      this.collectRegattaStartCandidatesFromStates()
    );
    if (Number.isFinite(clusteredStart)) {
      this.regattaReferenceStartMs = clusteredStart;
    }

    return this.regattaReferenceStartMs;
  }

  collectRegattaStartCandidatesFromEvents() {
    const validStarts = [];

    this.precomputedEvents.forEach(events => {
      if (!events?.startTime) {
        return;
      }

      const startMs = events.startTime.getTime();
      if (!Number.isFinite(startMs)) {
        return;
      }

      validStarts.push(startMs);
    });

    return validStarts;
  }

  collectRegattaStartCandidatesFromStates() {
    const validStarts = [];

    this.aircraftStates.forEach(state => {
      if (
        (state?.raceStatus !== 'racing' && state?.raceStatus !== 'finished') ||
        !Number.isFinite(state?.startTimeMs)
      ) {
        return;
      }

      validStarts.push(state.startTimeMs);
    });

    return validStarts;
  }

  resolveClusteredRegattaStartMs(startCandidates) {
    if (!Array.isArray(startCandidates) || startCandidates.length === 0) {
      return null;
    }

    const cluster = findDominantStartCluster(startCandidates);
    if (!cluster) {
      return null;
    }

    if (cluster.count >= 2) {
      return cluster.earliestMs;
    }

    return startCandidates.length === 1 ? cluster.earliestMs : null;
  }
}

/**
 * Helper: Format seconds to HH:MM:SS
 * @param {number} seconds - Total seconds
 * @returns {string} Formatted time
 */
function formatElapsedTime(seconds) {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  } else {
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
}

function coerceToTimestampMs(value) {
  if (value === null || value === undefined) {
    return NaN;
  }

  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? NaN : ms;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : NaN;
  }

  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? NaN : parsed;
  }

  if (typeof value === 'object') {
    // Attempt to extract nested timestamp fields
    if ('timestamp' in value) {
      return coerceToTimestampMs(value.timestamp);
    }
    if ('time_ms' in value) {
      return coerceToTimestampMs(value.time_ms);
    }
    if ('time_epoch_ms' in value) {
      return coerceToTimestampMs(value.time_epoch_ms);
    }
    if ('timeEpochMs' in value) {
      return coerceToTimestampMs(value.timeEpochMs);
    }
  }

  return NaN;
}

function extractTimestampMs(position) {
  if (!position) {
    return Date.now();
  }

  const fromTimestamp = coerceToTimestampMs(position);
  if (Number.isFinite(fromTimestamp)) {
    return fromTimestamp;
  }

  const direct = coerceToTimestampMs(position.timestamp);
  if (Number.isFinite(direct)) {
    return direct;
  }

  const timeMs = coerceToTimestampMs(position.time_ms);
  if (Number.isFinite(timeMs)) {
    return timeMs;
  }

  const timeEpochMs = coerceToTimestampMs(position.time_epoch_ms);
  if (Number.isFinite(timeEpochMs)) {
    return timeEpochMs;
  }

  const camelEpoch = coerceToTimestampMs(position.timeEpochMs);
  if (Number.isFinite(camelEpoch)) {
    return camelEpoch;
  }

  return Date.now();
}

function resolveTaskStartGateMs(task) {
  if (!task) {
    return null;
  }

  const candidates = [
    task.start_time_override_iso,
    task.startTimeOverrideIso,
    task.manual_start_time_iso
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    const candidateMs = new Date(candidate).getTime();
    if (Number.isFinite(candidateMs)) {
      return candidateMs;
    }
  }

  return null;
}

function hasExplicitNonRegattaStartWindow(task) {
  if (!task) {
    return false;
  }

  const rawStartWindowMinutes = task.start_window_minutes ?? task.startWindowMinutes;
  const startWindowMinutes = Number.parseFloat(rawStartWindowMinutes);
  return Number.isFinite(startWindowMinutes) && startWindowMinutes > 0;
}

function filterEligibleStartCandidates(events, startFloorMs = null) {
  if (!events?.startCandidates?.length) {
    return [];
  }

  const firstTPTime = events.tpTimes?.find(t => t !== null);
  const firstTPTimeMs = firstTPTime instanceof Date ? firstTPTime.getTime() : NaN;

  return events.startCandidates.filter((candidate) => {
    const candidateMs = candidate?.time instanceof Date ? candidate.time.getTime() : NaN;
    if (!Number.isFinite(candidateMs)) {
      return false;
    }

    const isBeforeFirstTp = candidate.beforeAnyTP || (Number.isFinite(firstTPTimeMs) && candidateMs < firstTPTimeMs);
    if (!isBeforeFirstTp) {
      return false;
    }

    if (Number.isFinite(startFloorMs) && candidateMs < startFloorMs) {
      return false;
    }

    return true;
  });
}

function buildEmptyReplayAatEventBuckets(task) {
  const count = Array.isArray(task?.turnpoints) ? task.turnpoints.length : 0;
  return Array.from({ length: count }, () => []);
}

function getReplayTaskPoint(task, tpIndex) {
  const turnpoint = task?.turnpoints?.[tpIndex];
  if (!turnpoint) {
    return null;
  }

  const scoredLat = Number(turnpoint.scoredLat);
  const scoredLon = Number(turnpoint.scoredLon);
  if (Number.isFinite(scoredLat) && Number.isFinite(scoredLon)) {
    return {
      lat: scoredLat,
      lon: scoredLon
    };
  }

  return {
    lat: turnpoint.Lat,
    lon: turnpoint.Lon
  };
}

function getReplayTurnpointPoint(turnpoint) {
  return {
    lat: turnpoint?.Lat,
    lon: turnpoint?.Lon
  };
}

function getReplayAatScoredPoint(run, task, tpIndex) {
  if (tpIndex <= 0 || tpIndex >= task.turnpoints.length - 1) {
    return getReplayTaskPoint(task, tpIndex);
  }

  return run.aatScoredPoints?.[tpIndex] || getReplayTurnpointPoint(task.turnpoints[tpIndex]);
}

function updateReplayRunAatScore(run, position, task) {
  if (task?.task_type !== 'AAT') {
    return;
  }

  const tpIndex = run.lastTPReached;
  if (tpIndex < 1 || tpIndex >= task.turnpoints.length - 1) {
    return;
  }

  const turnpoint = task.turnpoints[tpIndex];
  const candidatePoint = { lat: position.lat, lon: position.lon };
  if (!isInsideSector(candidatePoint, turnpoint)) {
    return;
  }

  const previousPoint =
    tpIndex === 1
      ? getReplayTaskPoint(task, 0)
      : getReplayAatScoredPoint(run, task, tpIndex - 1);
  const nextPoint = getReplayTaskPoint(task, tpIndex + 1);
  if (!previousPoint || !nextPoint) {
    return;
  }

  const candidateScore_m =
    haversineDistance(previousPoint.lat, previousPoint.lon, candidatePoint.lat, candidatePoint.lon) +
    haversineDistance(candidatePoint.lat, candidatePoint.lon, nextPoint.lat, nextPoint.lon);
  const previousScore_m = run.aatScoredDistances_m?.[tpIndex];

  if (
    Number.isFinite(previousScore_m) &&
    candidateScore_m <= previousScore_m + AAT_SCORE_EPSILON_M
  ) {
    return;
  }

  run.aatScoredPoints[tpIndex] = candidatePoint;
  run.aatScoredDistances_m[tpIndex] = candidateScore_m;

  const eventTimeMs = extractTimestampMs(position);
  if (!Number.isFinite(eventTimeMs)) {
    return;
  }

  run.aatScoredPointEvents[tpIndex].push({
    timeMs: eventTimeMs,
    point: candidatePoint,
    score_m: candidateScore_m
  });
}

function calculateReplayPathDistanceKm(points) {
  if (!Array.isArray(points) || points.length < 2) {
    return 0;
  }

  let totalDistance_m = 0;
  for (let index = 1; index < points.length; index += 1) {
    totalDistance_m += haversineDistance(
      points[index - 1].lat,
      points[index - 1].lon,
      points[index].lat,
      points[index].lon
    );
  }

  return totalDistance_m / 1000;
}

function calculateReplayRunAatFinishDistanceKm(run, task) {
  const pathPoints = [getReplayTaskPoint(task, 0)];
  const lastCompletedIndex = Math.min(run.lastTPReached, task.turnpoints.length - 2);

  for (let tpIndex = 1; tpIndex <= lastCompletedIndex; tpIndex += 1) {
    pathPoints.push(getReplayAatScoredPoint(run, task, tpIndex));
  }

  pathPoints.push(getReplayTaskPoint(task, task.turnpoints.length - 1));
  return Math.max(0, calculateReplayPathDistanceKm(pathPoints));
}

function getLatestReplayAatScoreEvent(events, targetMs) {
  if (!Array.isArray(events) || !events.length || !Number.isFinite(targetMs)) {
    return null;
  }

  let left = 0;
  let right = events.length - 1;
  let bestIndex = -1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const eventTimeMs = Number(events[mid]?.timeMs);
    if (!Number.isFinite(eventTimeMs)) {
      right = mid - 1;
      continue;
    }

    if (eventTimeMs <= targetMs) {
      bestIndex = mid;
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  return bestIndex >= 0 ? events[bestIndex] : null;
}

function buildReplayRunsFromCandidates(positions, task, startCandidates, startFloorMs = null) {
  if (!Array.isArray(positions) || positions.length === 0 || !task?.turnpoints || task.turnpoints.length < 2) {
    return [];
  }

  const intermediateTpCount = Math.max(0, task.turnpoints.length - 2);
  const requiredTPIndex = task.turnpoints.length - 2;
  const finishSector = task.turnpoints[task.turnpoints.length - 1];
  const isAatTask = task?.task_type === 'AAT';
  const afterFloor = [];
  let lastBeforeFloor = null;

  for (const candidate of startCandidates) {
    const candidateMs = candidate?.time instanceof Date ? candidate.time.getTime() : NaN;
    if (!Number.isFinite(candidateMs)) continue;
    if (Number.isFinite(startFloorMs) && candidateMs < startFloorMs) {
      lastBeforeFloor = candidate;
    } else {
      afterFloor.push(candidate);
    }
  }

  // If a startFloor is set and no candidates exist after it (or the first post-floor
  // candidate is much later), inject a synthetic start at the floor time using the
  // last pre-floor crossing. This handles regatta races where pilots cross the start
  // line while thermalling before the gate opens and are already past it at gate time.
  // In regatta mode the pilot only starts once, so discard later crossings — they're
  // artefacts of the pilot's track passing near the start area during the task.
  if (Number.isFinite(startFloorMs) && lastBeforeFloor) {
    const firstAfterMs = afterFloor.length > 0 ? afterFloor[0].time.getTime() : Infinity;
    const gapAfterFloor = firstAfterMs - startFloorMs;
    // If there's no post-floor candidate within 10 minutes, inject one at the floor time
    // and drop all later re-crossings so a single run covers the entire flight.
    if (gapAfterFloor > 10 * 60 * 1000) {
      afterFloor.length = 0; // clear later re-crossings
      afterFloor.push({
        time: new Date(startFloorMs),
        altitude_m: lastBeforeFloor.altitude_m,
        belowMaxAlt: lastBeforeFloor.belowMaxAlt,
        beforeAnyTP: lastBeforeFloor.beforeAnyTP,
        _synthetic: true
      });
    }
  }

  const candidates = afterFloor.sort((a, b) => a.time.getTime() - b.time.getTime());

  const runs = [];

  for (let runIndex = 0; runIndex < candidates.length; runIndex += 1) {
    const candidate = candidates[runIndex];
    const startTimeMs = candidate.time.getTime();
    const nextCandidate = candidates[runIndex + 1] || null;
    let nextStartTimeMs = nextCandidate?.time instanceof Date ? nextCandidate.time.getTime() : null;
    const run = {
      startTime: candidate.time,
      startTimeMs,
      startAltitude_m: candidate.altitude_m,
      belowMaxAlt: candidate.belowMaxAlt,
      tpTimes: Array.from({ length: intermediateTpCount }, () => null),
      finishTime: null,
      nextStartTimeMs,
      lastTPReached: -1,
      lastPosition: null,
      isFinalRun: runIndex === candidates.length - 1,
      isDisplayable: true,
      aatScoredPoints: Array.from({ length: task.turnpoints.length }, () => null),
      aatScoredDistances_m: Array.from({ length: task.turnpoints.length }, () => null),
      aatScoredPointEvents: buildEmptyReplayAatEventBuckets(task),
      aatFinishOfficialDistance_km: null,
      aatFinishRealDistance_km: null
    };

    const wasInsideTP = new Array(task.turnpoints.length).fill(false);
    let wasInsideFinish = false;
    let lastFinishPosition = null;

    let _posCount = 0;
    for (const position of positions) {
      const eventTimeMs = extractTimestampMs(position);
      if (!Number.isFinite(eventTimeMs) || eventTimeMs < startTimeMs) {
        continue;
      }
      if (Number.isFinite(nextStartTimeMs) && eventTimeMs >= nextStartTimeMs) {
        // Don't truncate this run if the pilot has already reached all intermediate TPs
        // and is approaching the finish — the re-crossing of the start line is an artefact
        // of the track passing near the start area on the way to the finish.
        if (run.lastTPReached >= requiredTPIndex) {
          // Extend this run past the next start crossing — keep scanning for the finish.
          // Null out the boundary so we don't break again at the same threshold.
          nextStartTimeMs = candidates[runIndex + 1 + (run._absorbed || 0) + 1]?.time?.getTime() ?? null;
          run._absorbed = (run._absorbed || 0) + 1;
        } else {
          break;
        }
      }

      run.lastPosition = position;
      _posCount++;

      let nextTPIndex = run.lastTPReached + 1;
      if (nextTPIndex === 0) {
        nextTPIndex = 1;
      }

      if (nextTPIndex >= 1 && nextTPIndex < task.turnpoints.length - 1) {
        const nextTP = task.turnpoints[nextTPIndex];
        const nowInsideTP = isInsideSector({ lat: position.lat, lon: position.lon }, nextTP);
        const justEnteredTP = !wasInsideTP[nextTPIndex] && nowInsideTP;

        if (justEnteredTP) {
          run.tpTimes[nextTPIndex - 1] = new Date(position.timestamp);
          run.lastTPReached = nextTPIndex;
        }

        wasInsideTP[nextTPIndex] = nowInsideTP;
      }

      if (isAatTask) {
        updateReplayRunAatScore(run, position, task);
      }

      const nowInsideFinish = isPositionInsideFinish(position, finishSector);
      if (!run.finishTime && run.lastTPReached >= requiredTPIndex) {
        const finishEventMs = findFinishEntryMs(
          lastFinishPosition,
          position,
          finishSector,
          null,
          wasInsideFinish
        );
        const crossedFinish = Number.isFinite(finishEventMs);
        if (crossedFinish) {
          run.finishTime = new Date(finishEventMs);
          if (isAatTask) {
            const finishDistance_km = calculateReplayRunAatFinishDistanceKm(run, task);
            run.aatFinishOfficialDistance_km = finishDistance_km;
            run.aatFinishRealDistance_km = finishDistance_km;
          }
        }
      }

      wasInsideFinish = nowInsideFinish;
      lastFinishPosition = position;
    }

    // If this run absorbed subsequent start crossings, skip them in the outer loop
    // and update isFinalRun since this run now extends further.
    if (run._absorbed > 0) {
      runIndex += run._absorbed;
      run.isFinalRun = runIndex >= candidates.length - 1;
      run.nextStartTimeMs = null; // run extends to end of track
    }

    if (run.isFinalRun) {
      run.isDisplayable = isReplayRunDisplayableAtTrackEnd(run, task);
    }

    runs.push(run);

    // Condor scoring treats the first completed finish as terminal; ignore any
    // later start candidates after a completed run.
    if (run.finishTime) {
      break;
    }
  }

  return runs;
}

function applyReplayRunToEvents(events, run) {
  events.startTime = null;
  events.startAltitude_m = null;
  events.tpTimes = Array.isArray(events.tpTimes) ? events.tpTimes.map(() => null) : [];
  events.finishTime = null;
  events.aatScoredPointEvents = Array.isArray(events.aatScoredPointEvents)
    ? events.aatScoredPointEvents.map(() => [])
    : [];
  events.aatFinishOfficialDistance_km = null;
  events.aatFinishRealDistance_km = null;

  if (!run) {
    return;
  }

  events.startTime = run.startTime;
  events.startAltitude_m = run.startAltitude_m;
  events.tpTimes = Array.isArray(run.tpTimes) ? [...run.tpTimes] : [];
  events.finishTime = run.finishTime || null;
  events.aatScoredPointEvents = Array.isArray(run.aatScoredPointEvents)
    ? run.aatScoredPointEvents.map((eventList) => Array.isArray(eventList) ? [...eventList] : [])
    : [];
  events.aatFinishOfficialDistance_km = Number.isFinite(run.aatFinishOfficialDistance_km)
    ? run.aatFinishOfficialDistance_km
    : null;
  events.aatFinishRealDistance_km = Number.isFinite(run.aatFinishRealDistance_km)
    ? run.aatFinishRealDistance_km
    : events.aatFinishOfficialDistance_km;
}

function selectAuthoritativeReplayRun(runs) {
  if (!Array.isArray(runs) || runs.length === 0) {
    return null;
  }

  // Pick the run with the most task progress: prefer finished runs first,
  // then highest lastTPReached, then longest time span as tiebreaker.
  let best = null;
  for (const run of runs) {
    if (run?.isFinalRun && run.isDisplayable === false) {
      continue;
    }
    if (!best) {
      best = run;
      continue;
    }
    const bestFinished = best.finishTime ? 1 : 0;
    const runFinished = run.finishTime ? 1 : 0;
    if (runFinished > bestFinished) {
      best = run;
    } else if (runFinished === bestFinished) {
      const bestTP = best.lastTPReached ?? -1;
      const runTP = run.lastTPReached ?? -1;
      if (runTP > bestTP) {
        best = run;
      } else if (runTP === bestTP) {
        // Equal progress — prefer run covering the most time (longest segment).
        // nextStartTimeMs is the end boundary (null for the final run).
        const bestEnd = best.nextStartTimeMs ?? (best.lastPosition ? extractTimestampMs(best.lastPosition) : best.startTimeMs);
        const runEnd = run.nextStartTimeMs ?? (run.lastPosition ? extractTimestampMs(run.lastPosition) : run.startTimeMs);
        const bestSpan = (bestEnd || best.startTimeMs) - best.startTimeMs;
        const runSpan = (runEnd || run.startTimeMs) - run.startTimeMs;
        if (runSpan > bestSpan) {
          best = run;
        }
      }
    }
  }

  return best || runs[runs.length - 1] || null;
}

function findLatestReplayPositionAtOrBeforeTime(positions, targetTime) {
  if (!Array.isArray(positions) || positions.length === 0 || !targetTime) {
    return null;
  }

  const targetMs = targetTime instanceof Date ? targetTime.getTime() : coerceToTimestampMs(targetTime);
  if (!Number.isFinite(targetMs)) {
    return null;
  }

  let left = 0;
  let right = positions.length - 1;
  let bestIndex = -1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const eventTimeMs = extractTimestampMs(positions[mid]);

    if (eventTimeMs <= targetMs) {
      bestIndex = mid;
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  return bestIndex >= 0 ? positions[bestIndex] : null;
}

function resolveReplayRunAtTime(events, targetMs) {
  if (!events?.runs?.length || !Number.isFinite(targetMs)) {
    return null;
  }

  let activeRun = null;
  for (const run of events.runs) {
    if (!Number.isFinite(run?.startTimeMs) || run.startTimeMs > targetMs) {
      break;
    }
    if (run?.isFinalRun && run.isDisplayable === false) {
      continue;
    }
    activeRun = run;
  }

  return activeRun;
}

function isReplayRunDisplayableAtTrackEnd(run, task) {
  if (!run) {
    return false;
  }
  if (run.finishTime) {
    return true;
  }
  if (Array.isArray(run.tpTimes) && run.tpTimes.some(Boolean)) {
    return true;
  }

  const lastPositionMs = extractTimestampMs(run.lastPosition);
  if (!Number.isFinite(lastPositionMs) || !Number.isFinite(run.startTimeMs)) {
    return false;
  }
  if ((lastPositionMs - run.startTimeMs) < 10000) {
    return false;
  }

  const progressMeters = getReplayRunFirstLegProgressMeters(run.lastPosition, task);
  if (!Number.isFinite(progressMeters)) {
    return false;
  }

  const minimumProgressMeters = getReplayRunMinimumProgressMeters(task);
  return progressMeters >= minimumProgressMeters;
}

function getReplayRunMinimumProgressMeters(task) {
  const startSector = task?.turnpoints?.[0];
  const nextTP = task?.turnpoints?.[1];
  if (!startSector || !nextTP) {
    return 750;
  }

  const legDistanceMeters = haversineDistance(startSector.Lat, startSector.Lon, nextTP.Lat, nextTP.Lon);
  return Math.max(500, Math.min(2000, legDistanceMeters * 0.02));
}

function getReplayRunFirstLegProgressMeters(position, task) {
  const startSector = task?.turnpoints?.[0];
  const nextTP = task?.turnpoints?.[1];
  if (!position || !startSector || !nextTP) {
    return null;
  }

  return projectedDistance(
    startSector.Lat,
    startSector.Lon,
    nextTP.Lat,
    nextTP.Lon,
    position.lat,
    position.lon
  );
}

function isHeadingTowardNextTurnpoint(previousPosition, currentPosition, task) {
  const nextTurnpoint = task?.turnpoints?.[1];
  if (!nextTurnpoint || !currentPosition) {
    return true;
  }

  const headingDeg = getTrackHeadingDegrees(previousPosition, currentPosition);
  if (!Number.isFinite(headingDeg)) {
    return true;
  }

  const bearingToNextTp = calculateBearing(
    currentPosition.lat,
    currentPosition.lon,
    nextTurnpoint.Lat,
    nextTurnpoint.Lon
  );
  const angleDelta = Math.abs(normalizeAngle(headingDeg - bearingToNextTp));
  return angleDelta <= 100;
}

function getTrackHeadingDegrees(previousPosition, currentPosition) {
  if (previousPosition && currentPosition) {
    const prevLat = Number(previousPosition.lat);
    const prevLon = Number(previousPosition.lon);
    const currLat = Number(currentPosition.lat);
    const currLon = Number(currentPosition.lon);
    if (
      Number.isFinite(prevLat) && Number.isFinite(prevLon)
      && Number.isFinite(currLat) && Number.isFinite(currLon)
    ) {
      const distanceMeters = haversineDistance(prevLat, prevLon, currLat, currLon);
      if (distanceMeters >= 25) {
        return calculateBearing(prevLat, prevLon, currLat, currLon);
      }
    }
  }

  const fallbackHeading = Number(currentPosition?.heading_deg);
  return Number.isFinite(fallbackHeading) ? fallbackHeading : null;
}

function isPositionInsideFinish(position, finishSector, minFinishAltitude_m = null) {
  if (!position || !finishSector) {
    return false;
  }

  const lat = Number(position.lat);
  const lon = Number(position.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return false;
  }

  if (!isInsideSector({ lat, lon }, finishSector)) {
    return false;
  }

  if (minFinishAltitude_m !== null && minFinishAltitude_m !== undefined) {
    const altitude_m = Number(position.altitude_m);
    if (!Number.isFinite(altitude_m) || altitude_m < minFinishAltitude_m) {
      return false;
    }
  }

  return true;
}

function findFinishEntryMs(previousPosition, currentPosition, finishSector, minFinishAltitude_m = null, wasInsideFinish = false) {
  const currentMs = coerceToTimestampMs(currentPosition);
  if (!currentPosition || !Number.isFinite(currentMs)) {
    return NaN;
  }

  const currentInside = isPositionInsideFinish(currentPosition, finishSector, minFinishAltitude_m);
  if (!previousPosition) {
    return !wasInsideFinish && currentInside ? Math.round(currentMs) : NaN;
  }

  const previousMs = coerceToTimestampMs(previousPosition);
  if (!Number.isFinite(previousMs) || currentMs <= previousMs) {
    return !wasInsideFinish && currentInside ? Math.round(currentMs) : NaN;
  }

  const previousInside = wasInsideFinish || isPositionInsideFinish(previousPosition, finishSector, minFinishAltitude_m);
  if (previousInside) {
    return NaN;
  }

  let lowerFraction = 0;
  let upperFraction = null;

  for (let step = 1; step <= FINISH_SEGMENT_SCAN_STEPS; step += 1) {
    const fraction = step / FINISH_SEGMENT_SCAN_STEPS;
    const sample = interpolateTrackPosition(previousPosition, currentPosition, fraction);
    if (isPositionInsideFinish(sample, finishSector, minFinishAltitude_m)) {
      lowerFraction = (step - 1) / FINISH_SEGMENT_SCAN_STEPS;
      upperFraction = fraction;
      break;
    }
  }

  if (upperFraction === null) {
    return NaN;
  }

  for (let step = 0; step < FINISH_CROSSING_BINARY_STEPS; step += 1) {
    const fraction = (lowerFraction + upperFraction) / 2;
    const sample = interpolateTrackPosition(previousPosition, currentPosition, fraction);
    if (isPositionInsideFinish(sample, finishSector, minFinishAltitude_m)) {
      upperFraction = fraction;
    } else {
      lowerFraction = fraction;
    }
  }

  return Math.round(previousMs + (currentMs - previousMs) * upperFraction);
}

function estimateFinishCrossingMs(previousPosition, currentPosition, finishSector, minFinishAltitude_m = null) {
  const finishEntryMs = findFinishEntryMs(previousPosition, currentPosition, finishSector, minFinishAltitude_m);
  if (Number.isFinite(finishEntryMs)) {
    return finishEntryMs;
  }

  const currentMs = coerceToTimestampMs(currentPosition);
  if (
    !previousPosition ||
    !currentPosition ||
    !Number.isFinite(currentMs)
  ) {
    return extractTimestampMs(currentPosition);
  }

  const previousMs = coerceToTimestampMs(previousPosition);
  if (!Number.isFinite(previousMs) || currentMs <= previousMs) {
    return extractTimestampMs(currentPosition);
  }

  const previousInside = isPositionInsideFinish(previousPosition, finishSector, minFinishAltitude_m);
  const currentInside = isPositionInsideFinish(currentPosition, finishSector, minFinishAltitude_m);
  if (previousInside || !currentInside) {
    return Math.round(currentMs);
  }

  let lowerFraction = 0;
  let upperFraction = 1;
  let bracketFound = false;

  for (let step = 1; step <= FINISH_CROSSING_BRACKET_STEPS; step += 1) {
    const fraction = step / FINISH_CROSSING_BRACKET_STEPS;
    const sample = interpolateTrackPosition(previousPosition, currentPosition, fraction);
    if (isPositionInsideFinish(sample, finishSector, minFinishAltitude_m)) {
      lowerFraction = (step - 1) / FINISH_CROSSING_BRACKET_STEPS;
      upperFraction = fraction;
      bracketFound = true;
      break;
    }
  }

  if (!bracketFound) {
    return Math.round(currentMs);
  }

  for (let step = 0; step < FINISH_CROSSING_BINARY_STEPS; step += 1) {
    const fraction = (lowerFraction + upperFraction) / 2;
    const sample = interpolateTrackPosition(previousPosition, currentPosition, fraction);
    if (isPositionInsideFinish(sample, finishSector, minFinishAltitude_m)) {
      upperFraction = fraction;
    } else {
      lowerFraction = fraction;
    }
  }

  return Math.round(previousMs + (currentMs - previousMs) * upperFraction);
}

function interpolateTrackPosition(previousPosition, currentPosition, fraction) {
  return {
    lat: interpolateNumber(previousPosition?.lat, currentPosition?.lat, fraction),
    lon: interpolateNumber(previousPosition?.lon, currentPosition?.lon, fraction),
    altitude_m: interpolateNumber(previousPosition?.altitude_m, currentPosition?.altitude_m, fraction)
  };
}

function interpolateNumber(startValue, endValue, fraction) {
  const start = Number(startValue);
  const end = Number(endValue);

  if (!Number.isFinite(start) && !Number.isFinite(end)) {
    return NaN;
  }
  if (!Number.isFinite(start)) {
    return end;
  }
  if (!Number.isFinite(end)) {
    return start;
  }

  return start + (end - start) * fraction;
}

function findDominantStartCluster(startCandidates) {
  if (!Array.isArray(startCandidates) || startCandidates.length === 0) {
    return null;
  }

  const starts = startCandidates
    .filter(Number.isFinite)
    .sort((a, b) => a - b);
  if (starts.length === 0) {
    return null;
  }

  let windowStart = 0;
  let bestStartIndex = 0;
  let bestEndIndex = 0;

  for (let windowEnd = 0; windowEnd < starts.length; windowEnd += 1) {
    while (starts[windowEnd] - starts[windowStart] > REGATTA_START_CLUSTER_WINDOW_MS) {
      windowStart += 1;
    }

    const currentCount = windowEnd - windowStart + 1;
    const bestCount = bestEndIndex - bestStartIndex + 1;
    const isBetterCluster =
      currentCount > bestCount ||
      (currentCount === bestCount && starts[windowStart] < starts[bestStartIndex]);

    if (isBetterCluster) {
      bestStartIndex = windowStart;
      bestEndIndex = windowEnd;
    }
  }

  return {
    count: bestEndIndex - bestStartIndex + 1,
    earliestMs: starts[bestStartIndex],
    latestMs: starts[bestEndIndex]
  };
}

/**
 * Find the time window (of given width) containing crossings from the most distinct pilots.
 * @param {Array<{ms: number, cookie: string}>} crossings - sorted by ms
 * @param {number} windowMs - cluster window width in ms
 * @returns {{count: number, distinctPilots: number, earliestMs: number, latestMs: number}|null}
 */
function findDominantPilotCluster(crossings, windowMs) {
  if (!crossings || crossings.length === 0) return null;

  let bestDistinct = 0;
  let bestStart = 0;
  let bestEnd = 0;
  let winStart = 0;
  const pilotCount = new Map(); // cookie -> count in current window

  for (let winEnd = 0; winEnd < crossings.length; winEnd++) {
    // Add right edge
    pilotCount.set(crossings[winEnd].cookie, (pilotCount.get(crossings[winEnd].cookie) || 0) + 1);

    // Shrink left edge if window too wide
    while (crossings[winEnd].ms - crossings[winStart].ms > windowMs) {
      const c = crossings[winStart].cookie;
      const cnt = pilotCount.get(c) - 1;
      if (cnt <= 0) pilotCount.delete(c);
      else pilotCount.set(c, cnt);
      winStart++;
    }

    const distinct = pilotCount.size;
    if (distinct > bestDistinct) {
      bestDistinct = distinct;
      bestStart = winStart;
      bestEnd = winEnd;
    }
  }

  if (bestDistinct === 0) return null;
  return {
    count: bestEnd - bestStart + 1,
    distinctPilots: bestDistinct,
    earliestMs: crossings[bestStart].ms,
    latestMs: crossings[bestEnd].ms
  };
}

function hasSufficientRegattaStartCluster(cluster, totalPilots) {
  const count = cluster?.distinctPilots ?? cluster?.count;
  if (!Number.isFinite(count) || count < 2) {
    return false;
  }

  const requiredCount = resolveRegattaClusterRequirement(totalPilots);
  return count >= requiredCount;
}

function resolveRegattaClusterRequirement(totalPilots) {
  const count = Number.isFinite(totalPilots) ? Math.max(0, Math.floor(totalPilots)) : 0;
  if (count <= 1) {
    return 2;
  }
  if (count <= 3) {
    return count;
  }
  if (count <= 5) {
    return Math.max(3, Math.ceil(count * 0.6));
  }
  return Math.max(3, Math.ceil(count * 0.5));
}
